import Link from 'next/link';
import React from 'react';

import { StrapiImage } from '@/components/ui/strapi-image';
import { cn, formatNumber, truncate } from '@/lib/utils';
import { Product } from '@/types/types';

export const ProductItems = ({
  heading,
  sub_heading,
  products,
  locale,
  basePath = 'products',
}: {
  heading?: string;
  sub_heading?: string;
  products: Product[];
  locale: string;
  basePath?: string;
}) => {
  const sortedProducts = React.useMemo(() => {
    return products
      ? [...products].sort((a, b) => {
          const orderA = a.order ?? 0;
          const orderB = b.order ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          return (Number(a.id) || 0) - (Number(b.id) || 0);
        })
      : [];
  }, [products]);

  return (
    <div className="py-20">
      {heading && (
        <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900 dark:text-white mb-4 tracking-tight">
          {heading}
        </h2>
      )}
      {sub_heading && (
        <p className="text-neutral-500 dark:text-neutral-400 text-lg md:text-xl font-medium mb-12 max-w-2xl">
          {sub_heading}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
        {sortedProducts.map((product) => (
          <ProductItem
            key={'regular-product-item' + product.id}
            product={product}
            locale={locale}
            basePath={basePath}
          />
        ))}
      </div>
    </div>
  );
};

export const tagColorMap: Record<string, string> = {
  blue: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-900/50',
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/50',
  red: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900/50',
  purple: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-900/50',
  yellow: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/50',
  orange: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-900/50',
  pink: 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/30 dark:text-pink-400 dark:border-pink-900/50',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-900/50',
  gray: 'bg-neutral-50 text-neutral-700 border-neutral-200 dark:bg-neutral-800/30 dark:text-neutral-400 dark:border-neutral-700/50',
};

const ProductItem = ({
  product,
  locale,
  basePath,
}: {
  product: Product;
  locale: string;
  basePath: string;
}) => {
  return (
    <Link
      href={`/${locale}/${basePath}/${product.slug}` as never}
      className="group relative flex flex-col bg-white dark:bg-neutral-900/50 rounded-3xl overflow-hidden border border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-700 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        <StrapiImage
          src={product?.images?.[0].url}
          alt={product.name}
          width={600}
          height={450}
          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
        />
      </div>

      <div className="p-6 md:p-8 flex flex-col flex-1">
        {product.tags && product.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {product.tags.map((tag) => (
              <span
                key={tag.id}
                className={cn(
                  'px-2.5 py-0.5 text-xs font-semibold rounded-full border transition-colors duration-200',
                  tagColorMap[tag.color] || tagColorMap.blue
                )}
              >
                {tag.text}
              </span>
            ))}
          </div>
        )}
        <div className="flex justify-between items-start gap-4 mb-4">
          <h3 className="text-xl font-bold text-neutral-900 dark:text-white leading-tight line-clamp-2">
            {product.name}
          </h3>
          {product.show_price !== false && (
            <span className="shrink-0 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm text-sm font-semibold px-3 py-1.5 rounded-full">
              {formatNumber(product.price)} €
            </span>
          )}
        </div>
        <p className="text-neutral-600 dark:text-neutral-400 text-base leading-relaxed mt-auto">
          {truncate(product.description, 120)}
        </p>
      </div>
    </Link>
  );
};
